import { merge } from "lodash-es";
import type { MaybeRefOrGetter, Ref } from "vue";
import { average, prominent } from "color.js";
import { extractColors } from "extract-colors";

function trim(str: string, charlist?: string): string {
    var whitespace = [' ', '\n', '\r', '\t', '\f', '\x0b', '\xa0', '\u2000', '\u2001', '\u2002', '\u2003', '\u2004', '\u2005', '\u2006', '\u2007', '\u2008', '\u2009', '\u200A', '\u200B', '\u2028', '\u2029', '\u3000'].join('');
    var l = 0;
    var i = 0;
    str += '';

    if (charlist) {
        whitespace = (charlist + '').replace(/([[\]().?/*{}+$^:])/g, '$1');
    }

    l = str.length;
    for (i = 0; i < l; i++) {
        if (whitespace.indexOf(str.charAt(i)) === -1) {
            str = str.substring(i);
            break;
        }
    }

    l = str.length;
    for (i = l - 1; i >= 0; i--) {
        if (whitespace.indexOf(str.charAt(i)) === -1) {
            str = str.substring(0, i + 1);
            break;
        }
    }

    return whitespace.indexOf(str.charAt(0)) === -1 ? str : '';
}

function hexdec(hexString: string): number {
    hexString = (hexString + '').replace(/[^a-f0-9]/gi, '');
    return parseInt(hexString, 16);
}

function dechex(number: number): string {
    if (number < 0) {
        number = 0xffffffff + number + 1;
    }

    return parseInt(number.toString(), 10).toString(16);
}

export enum Lightness {
    DARK = "dark",
    LIGHT = "light",
}

export class Color {
    public r: number = 0;
    public g: number = 0;
    public b: number = 0;
    public opacity: number = 0;
    public lightness: Lightness = Lightness.DARK;
    public rgb: string = "rgb(0,0,0)";
    public hex: string = "#333333";
    public hsp: number = 0;

    /**
     * Initializes the color class
     *
     * @param color
     * @param opacity
     * @param $default
     */
    constructor(
        color: Color | string = "",
        opacity: number = 100,
        $default: string | Color = "",
    ) {
        if (!color) color = $default;
        this.set(color, opacity);
    }

    /**
     * Set the color
     *
     * @param color
     * @param opacity
     * @return Color
     */
    public set(color: string | Color = "", opacity: number = 100): Color {
        if (color instanceof Color) color = color.toHex();
        else if (!color) color = "";

        color = trim(color, "#");

        let r, g, b;
        let a = undefined;
        if ((color as string).length === 8) {
            r = (color as string)[0] + (color as string)[1];
            g = (color as string)[2] + (color as string)[3];
            b = (color as string)[4] + (color as string)[5];
            a = (color as string)[6] + (color as string)[7];
        } else if ((color as string).length === 6) {
            r = (color as string)[0] + (color as string)[1];
            g = (color as string)[2] + (color as string)[3];
            b = (color as string)[4] + (color as string)[5];
        } else if ((color as string).length === 3) {
            r = (color as string)[0] + (color as string)[0];
            g = (color as string)[1] + (color as string)[1];
            b = (color as string)[2] + (color as string)[2];
        } else {
            r = 0;
            g = 0;
            b = 0;
        }

        this.setR(typeof r === "string" ? Math.round(hexdec(r)) : r);
        this.setG(typeof g === "string" ? Math.round(hexdec(g)) : g);
        this.setB(typeof b === "string" ? Math.round(hexdec(b)) : b);

        if (a && typeof a === "string") {
            opacity = Math.round((100 / 255) * hexdec(a));
        }

        this.opacity = opacity;
        this.lightOrDark();
        return this;
    }

    /**
     * Set the R color
     *
     * @param r
     */
    public setR(r: number): Color {
        if (r < 0) this.r = 0;
        else if (r > 255) this.r = 255;
        else this.r = r;

        this.lightOrDark();
        return this;
    }

    /**
     * Set the G color
     *
     * @param g
     */
    public setG(g: number): Color {
        if (g < 0) this.g = 0;
        else if (g > 255) this.g = 255;
        else this.g = g;

        this.lightOrDark();
        return this;
    }

    /**
     * Set the B color
     *
     * @param b
     */
    public setB(b: number): Color {
        if (b < 0) this.b = 0;
        else if (b > 255) this.b = 255;
        else this.b = b;

        this.lightOrDark();
        return this;
    }

    /**
     * Set the opacity
     * @param opacity
     */
    public setOpacity(opacity: number): Color {
        if (opacity < 0) this.opacity = 0;
        else if (opacity > 100) this.opacity = 100;
        else this.opacity = opacity;

        this.lightOrDark();
        return this;
    }

    /**
     * Adjust the R color
     *
     * @param adjustment
     */
    public adjustR(adjustment: number): Color {
        const r = this.r + adjustment;

        if (r < 0) this.r = 0;
        else if (r > 255) this.r = 255;
        else this.r = r;

        this.lightOrDark();
        return this;
    }

    /**
     * Adjust the G color
     *
     * @param adjustment
     */
    public adjustG(adjustment: number): Color {
        const g = this.g + adjustment;

        if (g < 0) this.g = 0;
        else if (g > 255) this.g = 255;
        else this.g = g;

        this.lightOrDark();
        return this;
    }

    /**
     * Adjust the B color
     *
     * @param adjustment
     */
    public adjustB(adjustment: number): Color {
        const b = this.b + adjustment;

        if (b < 0) this.b = 0;
        else if (b > 255) this.b = 255;
        else this.b = b;

        this.lightOrDark();
        return this;
    }

    /**
     * Adjust the opacity
     *
     * @param adjustment
     */
    public adjustOpacity(adjustment: number): Color {
        const opacity = this.opacity + adjustment;

        if (opacity < 0) this.opacity = 0;
        else if (opacity > 100) this.opacity = 100;
        else this.opacity = opacity;

        this.lightOrDark();
        return this;
    }

    /**
     * Adjust the RGB value by a set amount
     *
     * @param adjustment
     * @param opacityAdjustment
     */
    public adjustRgb(adjustment: number, opacityAdjustment: number = 0): Color {
        this.adjustR(adjustment);
        this.adjustB(adjustment);
        this.adjustG(adjustment);
        this.adjustOpacity(opacityAdjustment);
        return this;
    }

    /**
     * Check if the color is light
     *
     * @param adjustHsp: number
     * @return bool
     */

    public isLight(adjustHsp: number = 0): boolean {
        return this.lightOrDark(adjustHsp) === Lightness.LIGHT;
    }

    /**
     * Check if the color is dark
     *
     * @param adjustHsp: number
     * @return bool
     */

    public isDark(adjustHsp: number = 0): boolean {
        return this.lightOrDark(adjustHsp) === Lightness.DARK;
    }

    /**
     * Convert color to hex
     *
     * @param includeOpacity
     */
    public toHex(includeOpacity: boolean = false): string {
        let hex = "#";

        [dechex(this.r), dechex(this.g), dechex(this.b)].forEach((part) => {
            if (part.toString() === "NaN") part = "00";
            if (part.length === 1) part = `0${part}`;
            hex += part;
        });

        if (includeOpacity) {
            let opacity = dechex(
                this.opacity > 0 ? Math.round((255 / 100) * this.opacity) : 0,
            );

            if (opacity.length === 1) opacity = `0${opacity}`;

            hex += opacity;
        }

        this.hex = hex;

        return this.hex;
    }

    /**
     * Convert color to RGB
     */
    public toRgb(
        includeOpacity: boolean = false,
        replacements: {
            r?: string | number;
            g?: string | number;
            b?: string | number;
            opacity?: string | number;
        } = {},
    ): string {
        const color = {
            r: replacements["r"] ?? this.r,
            g: replacements["g"] ?? this.g,
            b: replacements["b"] ?? this.b,
            opacity: replacements["opacity"] ?? this.opacity,
        };
        return (this.rgb = `rgb${includeOpacity ? "a" : ""}(${color.r}, ${color.g}, ${color.b}${includeOpacity ? `, ${typeof color.opacity === "string" ? color.opacity : `${color.opacity}%`}` : ""})`);
    }

    public toRgba(
        opacity: string | number = 100,
        adjustments: {
            r?: string| number;
            g?: string | number;
            b?: string | number;
        } = {},
    ): string {
        return this.toRgb(true, {
            opacity: opacity,
            r: adjustments.r ?? this.r,
            g: adjustments.g ?? this.g,
            b: adjustments.b ?? this.b,
        });
    }

    /**
     * When cast to string make the color a hex
     */
    toString() {
        return this.toHex();
    }

    /**
     * Compare the color to another
     *
     * @param color
     * @param range
     * @param adjustHsp
     */
    public isSimilarTo(
        color: string | Color,
        range: number = 0,
        adjustHsp: number = 0,
    ): boolean {
        color = color instanceof Color ? color : new Color(color);

        return Math.abs(this.hsp + adjustHsp - color.hsp) <= range;
    }

    /**
     * Check if the color is greater than another color
     *
     * @param color
     * @param adjustHsp
     */
    public greaterThan(color: string | Color, adjustHsp: number = 0): boolean {
        color = color instanceof Color ? color : new Color(color);

        return this.hsp + adjustHsp > color.hsp;
    }

    /**
     * Check if the color is greater than or equal to another
     * @param color
     * @param adjustHsp
     */
    public greaterThanOrEqualTo(
        color: string | Color,
        adjustHsp: number = 0,
    ): boolean {
        color = color instanceof Color ? color : new Color(color);

        return this.hsp + adjustHsp >= color.hsp;
    }

    /**
     * Check if the color is less than another color
     *
     * @param color
     * @param adjustHsp
     */
    public lessThan(color: string | Color, adjustHsp: number = 0): boolean {
        color = color instanceof Color ? color : new Color(color);

        return this.hsp + adjustHsp < color.hsp;
    }

    /**
     * Check if the color is less than or equal to another color
     * @param color
     * @param adjustHsp
     */
    public lessThanOrEqualTo(
        color: string | Color,
        adjustHsp: number = 0,
    ): boolean {
        color = color instanceof Color ? color : new Color(color);

        return this.hsp + adjustHsp <= color.hsp;
    }

    /**
     * Inverts the color
     */
    public invert(): Color {
        this.setR(255 - this.r);
        this.setG(255 - this.g);
        this.setB(255 - this.b);
        return this;
    }

    clone() {
        return new Color(this);
    }

    /**
     * Check if the color is light or dark
     *
     * @param adjustHsp: number
     * @return string
     */
    private lightOrDark(adjustHsp: number = 0): string {
        this.setHsp();
        this.toHex();
        this.toRgb();

        return (this.lightness =
            this.hsp + adjustHsp > 127.5 ? Lightness.LIGHT : Lightness.DARK);
    }

    /**
     * Set the HSP for the color
     */
    private setHsp(): Color {
        this.hsp = Math.sqrt(
            0.299 * (this.r * this.r) +
            0.587 * (this.g * this.g) +
            0.114 * (this.b * this.b),
        );

        return this;
    }

    public when(
        condition: (color: Color) => boolean,
        then: (color: Color) => any,
        anyways?: (color: Color) => any,
    ): any {
        if (condition(this)) {
            return then(this);
        }

        return anyways ? anyways(this) : undefined;
    }

    public isWhite() {
        return this.isSimilarTo("#fff");
    }

    public isBlack() {
        return this.isSimilarTo("#000");
    }
}


export function useColor(
    color: string | Color,
    opacity: number = 100,
    fallback: string | Color = "",
) {
    return new Color(color, opacity, fallback);
}

export interface ColorExtractorOptions {
    amount: number;
    group: number;
    sample: number;
    immediate: boolean;
    prominenceOptions?: Partial<{
        pixels: number;
        distance: number;
        colorValidator: (
            red: number,
            green: number,
            blue: number,
            alpha: number,
        ) => boolean;
        saturationDistance: number;
        lightnessDistance: number;
        hueDistance: number;
    }>;
}

const EXTRACTOR_DEFAULTS: ColorExtractorOptions = {
    amount: 3,
    group: 20,
    sample: 10,
    immediate: false,
};

export function useColorExtractor(
    source: MaybeRefOrGetter<string | HTMLImageElement>,
    options?: MaybeRefOrGetter<Partial<ColorExtractorOptions>>,
) {
    source = toRef(source) as Ref<string | HTMLImageElement>;
    options = toRef(options) as Ref<ColorExtractorOptions>;

    options.value = merge(EXTRACTOR_DEFAULTS, options.value ?? {});

    const pendingAverage = ref<boolean>(false);
    const pendingProminent = ref<boolean>(false);
    const error = ref<boolean>(false);
    const pending = computed(
        () => pendingAverage.value && pendingProminent.value,
    );
    const avarageOutput = ref<Color>(new Color("#000000"));
    const prominentOutput = ref<Color[]>([]);

    const calculate = async (item?: string | HTMLImageElement) => {
        if (item || source.value) {
            error.value = false;
            pendingAverage.value = true;
            pendingProminent.value = true;

            await Promise.allSettled([
                average(item ?? source.value, {
                    ...options.value,
                    format: "hex",
                })
                    .then((color: string) => {
                        avarageOutput.value = new Color(color);
                    })
                    .catch(() => {
                        error.value = true;
                    })
                    .finally(() => {
                        pendingAverage.value = false;
                    }),
                options.value.prominenceOptions
                    ? extractColors(item ?? source.value, {
                        crossOrigin: "anonymous",
                        ...options.value.prominenceOptions,
                    })
                        .then((colors: { hex: string }[]) => {
                            prominentOutput.value = colors.map(
                                (col) => new Color(col.hex),
                            );
                        })
                        .catch(() => {
                            error.value = true;
                        })
                        .finally(() => {
                            pendingProminent.value = false;
                        })
                    : prominent(item ?? source.value, {
                        ...options.value,
                        format: "hex",
                    })
                        .then((color: string | string[]) => {
                            prominentOutput.value = (
                                Array.isArray(color) ? color : [color]
                            ).map((col) => new Color(col));
                        })
                        .catch(() => {
                            error.value = true;
                        })
                        .finally(() => {
                            pendingProminent.value = false;
                        }),
            ]);
        }

        return {
            average: avarageOutput.value,
            prominent: prominentOutput.value,
            success: !error.value,
        };
    };

    if (options.value.immediate) {
        watchOnce([source, options], () => calculate());
    }

    return {
        source,
        options,
        error,
        pending,
        average: avarageOutput,
        prominent: prominentOutput,
        extract: calculate,
    };
}
